import { useMemo } from 'react';
import moment from 'moment';

import {
  useNewsItemsFilteredQuery,
  VersionStates,
  type PageNewsletterBlock,
} from '@/components/common/Providers/GraphqlProvider/generated';

interface UseNewsItemsFilteredProps {
  newsletterBlock?: PageNewsletterBlock;
  enabled?: boolean;
}

export const useNewsItemsFiltered = ({
  newsletterBlock,
  enabled = true,
}: UseNewsItemsFilteredProps) => {
  // Extract query variables from newsletterBlock configuration
  const queryVariables = useMemo(() => {
    if (!newsletterBlock) return {};

    const variables: any = {
      pagination: {
        skip: 0,
        take: 14,
      },
      orderBy: [
        {
          publicationDate: 'DESC',
        },
      ],
    };

    // Add contentSchemaId if contentSchema is configured
    if (newsletterBlock.contentSchema?.id) {
      variables.contentSchemaId = newsletterBlock.contentSchema.id;
    }

    // If hasDependencyFilter is true, we would need to get the current dependency
    // For now, we'll leave this undefined unless we have a specific dependency to filter by
    if (newsletterBlock.hasDependencyFilter) {
      // This would need to be implemented based on your dependency context
      // variables.dependencyId = currentDependencyId;
    }
    //only enables
    variables.where = {
      newsStatus: [VersionStates.Active],
      publicationDate: { _lte: moment().format() },
    };

    return variables;
  }, [newsletterBlock]);

  // Use the generated Apollo hook
  const { data, loading, error } = useNewsItemsFilteredQuery({
    variables: queryVariables,
    skip: !enabled || !newsletterBlock,
    fetchPolicy: 'cache-and-network',
  });

  // Transform and sort the news items
  const newsItems = useMemo(() => {
    if (!data?.newsItemsFiltered) return [];

    // Sort by createdAt in descending order (newest first)
    return [...data.newsItemsFiltered].sort(
      (a, b) => new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime()
    );
  }, [data?.newsItemsFiltered]);

  return {
    data: newsItems,
    isLoading: loading,
    error,
    hasData: newsItems.length > 0,
  };
};
